import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  DEFAULT_TIME_OFF_COLOR,
  OWNER_TIMEZONE,
  SLOT_LENGTHS,
  SLOT_LENGTH_LABELS,
  addDays,
  coloringTag,
  lessonColor,
  mergeSpans,
  monthRange,
  normalizeHex,
  spanHasPassed,
  spansFromCells,
  startOfWeek,
  tagColorMap,
  type AvailabilityWindow,
  type Booking,
  type BulkAction,
  type SlotLength,
  type TimeOff,
} from '@diana/shared'
import { api, ApiError } from '../../lib/api.js'
import { MONTH_NAMES, hhmm, isoDateIn, monthGrid, wallClockIn, weekDays, weekTitle, weekdayHeading } from '../../lib/datetime.js'
import { Column, TopTitle } from '../../classroom/Shell.js'
import { Button, Dot, ErrorLine, Status, Tabs } from '../../classroom/ui.js'
import { ColorPicker } from '../../components/ColorPicker.js'
import { StudioWeekGrid, type Block, type Cell } from './StudioWeekGrid.js'
import { SMALL_BTN, Swatch, hatch, plural } from './calendarUi.js'
import { CopyWeekDialog } from './CopyWeekDialog.js'
import { DayEditor } from './DayEditor.js'
import { HourEditor } from './HourEditor.js'

/**
 * Diana's calendar (Studio-Calendar.png), two ways:
 *
 *   month — the overview. Which days are open, how many people are booked.
 *   week  — the timetable, 08:00–20:00 by default and wider when the week
 *           needs it. Click an hour to put a student in, or paint many
 *           hours and say what they are.
 *
 * An hour is one of three things: offered on the website, time she does not
 * work, or simply empty. The week draws the first as a dashed „Wolne” block
 * and the second hatched, „Nie pracuję”.
 *
 * The grid also separates OPEN from BOOKABLE. They are not the same: a
 * window shorter than its lesson length, or one inside the minimum notice,
 * is open and yet sells nothing. The free slots counted here come from the
 * same function that serves the public page, so the two cannot disagree.
 *
 * Everything is wall-clock in HER timezone. Students see their own.
 */

type View = 'month' | 'week'
const VIEW_KEY = 'studio.calendar.view'
const DAY_START = 8
const DAY_END = 20

/** The lesson dot when neither the student nor a tag of theirs has a colour. */
const PLAIN_DOT = 'var(--color-class-sky-700)'

function readView(): View {
  try {
    return localStorage.getItem(VIEW_KEY) === 'week' ? 'week' : 'month'
  } catch {
    return 'month'
  }
}

/** A picked grid cell, as one string so the selection can live in a Set. */
const cellKey = (day: string, hour: number) => `${day}|${hour}`

function parseCellKey(key: string): { day: string; hour: number } {
  const [day = '', hour = '0'] = key.split('|')
  return { day, hour: Number(hour) }
}

/** 990 -> "16:30" */
function fmtMin(minutes: number): string {
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`
}

const minutesOf = (t: string) => {
  const [h = '0', m = '0'] = t.split(':')
  return Number(h) * 60 + Number(m)
}

/** Every whole hour a span touches, as grid keys. */
function hoursOf(day: string, startMin: number, endMin: number): string[] {
  const out: string[] = []
  for (let h = Math.floor(startMin / 60); h < Math.ceil(endMin / 60); h++) out.push(cellKey(day, h))
  return out
}

/**
 * An hour whose last minute is behind us. Opening one writes a window that
 * can never sell anything, so the grid refuses to paint it and the endpoint
 * refuses to store it — the same rule, asked in both places.
 */
function hourHasPassed(day: string, hour: number, now: { day: string; minutes: number }): boolean {
  return spanHasPassed({ date: day, endTime: fmtMin((hour + 1) * 60) }, now)
}

/** „Oksana Kowalenko” -> „Oksana K.”, „Andrij i Olha Melnyk” -> „Andrij i Olha M.”; a group name stays whole. */
function shortName(name: string): string {
  const parts = name.trim().split(/\s+/)
  const last = parts[parts.length - 1] ?? ''
  if (parts.length < 2 || last.endsWith('.') || !/^\p{Lu}/u.test(last)) return name
  return `${parts.slice(0, -1).join(' ')} ${last[0]}.`
}

type Span = { startMin: number; endMin: number }

/** `from` minus every span in `holes`, merged where pieces touch. */
function subtract(from: Span[], holes: Span[]): Span[] {
  let pieces = mergeSpans(from)
  for (const hole of holes) {
    const next: Span[] = []
    for (const p of pieces) {
      if (hole.endMin <= p.startMin || hole.startMin >= p.endMin) {
        next.push(p)
        continue
      }
      if (hole.startMin > p.startMin) next.push({ startMin: p.startMin, endMin: hole.startMin })
      if (hole.endMin < p.endMin) next.push({ startMin: hole.endMin, endMin: p.endMin })
    }
    pieces = next
  }
  return pieces.filter((p) => p.endMin - p.startMin >= 15)
}

/** Where a booking lands on her wall clock; one past midnight is cut at 24:00. */
function bookingSpan(b: Booking): { day: string; startMin: number; endMin: number } {
  const start = wallClockIn(b.startsAt, OWNER_TIMEZONE)
  const end = wallClockIn(b.endsAt, OWNER_TIMEZONE)
  return { day: start.day, startMin: start.minutes, endMin: end.day === start.day ? end.minutes : 24 * 60 }
}

/** „19–25 października” — the year only when it is not this one. */
function periodLabel(view: View, days: string[], year: number, month: number, thisYear: number): string {
  if (view === 'month') return `${MONTH_NAMES[month - 1]}${year === thisYear ? '' : ` ${year}`}`
  const title = weekTitle(days)
  return title.endsWith(` ${thisYear}`) ? title.slice(0, -` ${thisYear}`.length) : title
}

export function CalendarPage() {
  const today = isoDateIn(new Date(), OWNER_TIMEZONE)
  /** Recomputed each render — good enough for a grid whose cells are an hour wide. */
  const nowClock = wallClockIn(new Date(), OWNER_TIMEZONE)
  const nowDay = nowClock.day
  const nowMinutes = nowClock.minutes
  const [view, setView] = useState<View>(readView)
  /** The day the calendar is centred on. Month view uses its month, week view its week. */
  const [anchor, setAnchor] = useState(today)
  const [openDay, setOpenDay] = useState<string | null>(null)
  const [openHour, setOpenHour] = useState<{ day: string; hour: number } | null>(null)

  /** Painting hours: which are picked, and what to do with them. */
  const [selectMode, setSelectMode] = useState(false)
  /** Set when a gesture landed only on hours that have passed. */
  const [pastRefused, setPastRefused] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(() => new Set())
  const [bulkLength, setBulkLength] = useState<SlotLength>(60)
  const [copyingWeek, setCopyingWeek] = useState(false)

  useEffect(() => {
    try {
      localStorage.setItem(VIEW_KEY, view)
    } catch {
      /* private mode — fine */
    }
  }, [view])

  // Hours picked in one week must not quietly follow her into another.
  useEffect(() => setSelected(new Set()), [anchor, view])

  const [year, month] = anchor.split('-').map(Number) as [number, number, number]
  const cells = useMemo(() => monthGrid(year, month - 1, today), [year, month, today])
  const days = useMemo(() => weekDays(anchor), [anchor])

  const from = view === 'month' ? cells[0]!.iso : days[0]!
  const to = view === 'month' ? cells[cells.length - 1]!.iso : days[6]!

  const queryClient = useQueryClient()
  const { data, isLoading, isError } = useQuery({
    queryKey: ['studio', 'calendar', from, to],
    queryFn: () => api.studio.calendarWeek(from, to),
  })
  const clientsQuery = useQuery({ queryKey: ['studio', 'clients'], queryFn: api.studio.clients })
  const settingsQuery = useQuery({ queryKey: ['studio', 'settings'], queryFn: api.studio.settings })
  /** Only to name a lesson booked from the website („Lekcja w parze”). */
  const offersQuery = useQuery({ queryKey: ['studio', 'offers'], queryFn: api.studio.offers })

  const timeOffColor =
    normalizeHex(
      String(
        (
          (settingsQuery.data?.find((s) => s.key === 'calendar' && s.locale === null)?.value ?? {}) as {
            timeOffColor?: string
          }
        ).timeOffColor ?? '',
      ),
    ) ?? DEFAULT_TIME_OFF_COLOR

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['studio', 'calendar'] })
    queryClient.invalidateQueries({ queryKey: ['studio', 'accounting'] })
    queryClient.invalidateQueries({ queryKey: ['studio', 'dashboard'] })
    queryClient.invalidateQueries({ queryKey: ['slots'] })
  }

  const saveColor = useMutation({
    mutationFn: (hex: string) => api.studio.setSetting('calendar', { timeOffColor: hex }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['studio', 'settings'] }),
  })

  const bulk = useMutation({
    mutationFn: (action: BulkAction) =>
      api.studio.bulkAvailability({
        action,
        slotMinutes: bulkLength,
        spans: spansFromCells([...selected].map(parseCellKey)),
      }),
    onSuccess: () => {
      setSelected(new Set())
      invalidate()
    },
  })

  /**
   * ⌘- or Ctrl-click: pick this one hour, wherever she is. It turns painting
   * mode on by itself — she has already said what she wants by holding the
   * key, and making her find the button first is the step she asked to lose.
   */
  const togglePick = useCallback(
    (day: string, hour: number) => {
      if (hourHasPassed(day, hour, nowClock)) return setPastRefused(true)
      setPastRefused(false)
      setSelectMode(true)
      setSelected((prev) => {
        const next = new Set(prev)
        const key = cellKey(day, hour)
        if (next.has(key)) next.delete(key)
        else next.add(key)
        return next
      })
    },
    [nowClock],
  )

  const onSelectRange = useCallback((picked: Cell[], select: boolean) => {
    setPastRefused(false)
    setSelected((prev) => {
      const next = new Set(prev)
      for (const cell of picked) {
        const key = cellKey(cell.day, cell.hour)
        if (select) next.add(key)
        else next.delete(key)
      }
      return next
    })
  }, [])
  const refusePast = useCallback(() => setPastRefused(true), [])

  const addWindow = useMutation({ mutationFn: api.studio.addWindow, onSuccess: invalidate })
  const deleteWindow = useMutation({ mutationFn: api.studio.deleteWindow, onSuccess: invalidate })

  const tagColors = useMemo(() => tagColorMap(data?.tagStyles ?? []), [data])
  const clientById = useMemo(() => new Map((clientsQuery.data ?? []).map((c) => [c.id, c])), [clientsQuery.data])
  const offerTitle = useMemo(() => new Map((offersQuery.data ?? []).map((o) => [o.id, o.title])), [offersQuery.data])

  /** Windows, lessons and time off bucketed by the day they belong to. */
  const byDay = useMemo(() => {
    const map: Record<string, { windows: AvailabilityWindow[]; bookings: Booking[]; timeOff: TimeOff[] }> = {}
    const bucket = (key: string) => (map[key] ??= { windows: [], bookings: [], timeOff: [] })

    for (const w of data?.windows ?? []) bucket(w.date).windows.push(w)
    for (const t of data?.timeOff ?? []) bucket(t.date).timeOff.push(t)
    for (const b of data?.bookings ?? []) {
      if (b.status === 'cancelled') continue
      bucket(isoDateIn(b.startsAt, OWNER_TIMEZONE)).bookings.push(b)
    }
    return map
  }, [data])

  const visibleBookings = useMemo(() => (data?.bookings ?? []).filter((b) => b.status !== 'cancelled'), [data])
  // A tiny „L3” on a lesson block when a plan lesson is tied to it.
  const planMark = useMemo(() => {
    const out = new Map<string, string>()
    for (const p of data?.planItems ?? []) {
      const mark = p.lessonNumber !== null ? `L${p.lessonNumber}` : 'kurs'
      out.set(p.bookingId, out.has(p.bookingId) ? `${out.get(p.bookingId)}, ${mark}` : mark)
    }
    return out
  }, [data])

  /* ── open versus bookable ────────────────────────────────────────────
   * The point of showing both: an hour can be open and still sell nothing,
   * and until now that was invisible — which is exactly how a booking page
   * ends up empty while the calendar looks full.
   */
  const { bookableHours, problemHours } = useMemo(() => {
    const open = new Set<string>()
    const off = new Set<string>()
    const bookable = new Set<string>()
    const busy = new Set<string>()

    for (const w of data?.windows ?? []) {
      for (const key of hoursOf(w.date, minutesOf(w.startTime), minutesOf(w.endTime))) open.add(key)
    }
    for (const t of data?.timeOff ?? []) {
      for (const key of hoursOf(t.date, minutesOf(t.startTime), minutesOf(t.endTime))) off.add(key)
    }
    for (const slot of data?.freeSlots ?? []) {
      const start = wallClockIn(slot.startsAt, OWNER_TIMEZONE)
      const end = wallClockIn(slot.endsAt, OWNER_TIMEZONE)
      const endMin = end.day === start.day ? end.minutes : 24 * 60
      for (const key of hoursOf(start.day, start.minutes, endMin)) bookable.add(key)
    }
    for (const b of visibleBookings) {
      const s = bookingSpan(b)
      for (const key of hoursOf(s.day, s.startMin, s.endMin)) busy.add(key)
    }

    // Open, nothing bookable in it, nobody booked, not blocked, not already
    // past: the hours she believes are on the website and are not.
    const problem = new Set<string>()
    for (const key of open) {
      if (bookable.has(key) || busy.has(key) || off.has(key)) continue
      const { day, hour } = parseCellKey(key)
      if (hourHasPassed(day, hour, { day: nowDay, minutes: nowMinutes })) continue
      problem.add(key)
    }

    return { bookableHours: bookable, problemHours: problem }
  }, [data, visibleBookings, nowDay, nowMinutes])

  function shift(delta: number) {
    if (view === 'week') {
      setAnchor((a) => addDays(a, delta * 7))
    } else {
      const m = month - 1 + delta
      const y = year + Math.floor(m / 12)
      setAnchor(monthRange(y, ((m % 12) + 12) % 12).from)
    }
  }

  const pendingCount = visibleBookings.filter((b) => b.status === 'pending').length

  /* ── the blocks on the week ──────────────────────────────────────── */
  const blocks: Block[] = useMemo(() => {
    const out: Block[] = []
    const countIn = (set: Set<string>, day: string, s: Span) => hoursOf(day, s.startMin, s.endMin).filter((k) => set.has(k)).length

    for (const day of days) {
      const bucket = byDay[day]
      if (!bucket) continue
      const offSpans = mergeSpans(bucket.timeOff.map((t) => ({ startMin: minutesOf(t.startTime), endMin: minutesOf(t.endTime) })))
      const lessonSpans = bucket.bookings.map((b) => bookingSpan(b))

      // Hatched: the time she does not work.
      offSpans.forEach((s, i) => {
        const label = bucket.timeOff.find((t) => minutesOf(t.startTime) >= s.startMin && minutesOf(t.startTime) < s.endMin)?.label
        out.push({
          id: `off-${day}-${i}`,
          day,
          ...s,
          kind: 'off',
          title: label || 'Nie pracuję',
          style: hatch(timeOffColor),
        })
      })

      // Dashed: what is left of her open windows once lessons and time off are taken out.
      const openSpans = subtract(
        bucket.windows.map((w) => ({ startMin: minutesOf(w.startTime), endMin: minutesOf(w.endTime) })),
        [...offSpans, ...lessonSpans],
      )
      openSpans.forEach((s, i) => {
        const free = countIn(bookableHours, day, s)
        const hidden = countIn(problemHours, day, s)
        const gone = spanHasPassed({ date: day, endTime: fmtMin(s.endMin) }, { day: nowDay, minutes: nowMinutes })
        const freeText = `${free} ${plural(free, 'wolna godzina', 'wolne godziny', 'wolnych godzin')}`
        out.push({
          id: `open-${day}-${i}`,
          day,
          ...s,
          kind: 'open',
          title: 'Wolne',
          sub: gone
            ? 'minęło'
            : hidden > 0
              ? free > 0
                ? `${free} wolne · ${hidden} nie na stronie`
                : 'nie na stronie'
              : free > 0
                ? freeText
                : undefined,
          warn: !gone && hidden > 0,
        })
      })
    }

    for (const b of visibleBookings) {
      const s = bookingSpan(b)
      const client = b.clientId ? clientById.get(b.clientId) : undefined
      const painter = coloringTag(client, tagColors)
      const own = client?.color ? normalizeHex(client.color) : null
      const offer = b.offerId ? offerTitle.get(b.offerId) : undefined
      const time = `${fmtMin(s.startMin)}–${fmtMin(s.endMin)}`
      const open = () => setOpenHour({ day: s.day, hour: Math.floor(s.startMin / 60) })

      if (b.status === 'pending') {
        const what = offer || b.goals
        out.push({
          id: b.id,
          day: s.day,
          startMin: s.startMin,
          endMin: s.endMin,
          kind: 'request',
          title: shortName(b.name),
          sub: `prośba${what ? ` · ${what.charAt(0).toLowerCase()}${what.slice(1)}` : ''}`,
          label: `${b.name}, ${time} — prośba do potwierdzenia`,
          onClick: open,
        })
        continue
      }

      const kind = offer ? offer.charAt(0).toLowerCase() + offer.slice(1) : client ? 'lekcja' : 'zapis ze strony'
      const state = b.status === 'completed' ? 'odbyta' : b.status === 'no_show' ? 'nieobecność' : ''
      const mark = planMark.get(b.id)
      out.push({
        id: b.id,
        day: s.day,
        startMin: s.startMin,
        endMin: s.endMin,
        kind: 'lesson',
        title: shortName(b.name),
        sub: [state || kind, mark, painter].filter(Boolean).join(' · '),
        // The student's own colour wins, then their group's; a lesson with
        // neither keeps the plain blue dot of the mockup.
        dot: own || painter ? lessonColor(client ?? null, tagColors) : PLAIN_DOT,
        label: `${b.name}, ${time}${painter ? ` · ${painter}` : ''}${mark ? ` · plan: ${mark}` : ''}`,
        onClick: open,
      })
    }

    return out
  }, [days, byDay, visibleBookings, planMark, clientById, tagColors, offerTitle, timeOffColor, bookableHours, problemHours, nowDay, nowMinutes])

  /**
   * The grid stretches to whatever the week actually contains. A fixed
   * 08:00–20:00 window hid an hour the warning above was pointing at, which
   * is worse than a taller grid.
   */
  const bounds = useMemo(() => {
    let start = DAY_START
    let end = DAY_END
    const stretch = (startMin: number, endMin: number) => {
      start = Math.min(start, Math.floor(startMin / 60))
      end = Math.max(end, Math.ceil(endMin / 60))
    }
    for (const w of data?.windows ?? []) stretch(minutesOf(w.startTime), minutesOf(w.endTime))
    for (const t of data?.timeOff ?? []) stretch(minutesOf(t.startTime), minutesOf(t.endTime))
    for (const b of visibleBookings) {
      const s = bookingSpan(b)
      stretch(s.startMin, s.endMin)
    }
    return { start: Math.max(0, start), end: Math.min(24, Math.max(end, start + 1)) }
  }, [data, visibleBookings])

  /** True when some run of picked hours is shorter than one lesson. */
  const tooShortForLesson = useMemo(() => {
    const byDate = new Map<string, { startMin: number; endMin: number }[]>()
    for (const span of spansFromCells([...selected].map(parseCellKey))) {
      const list = byDate.get(span.date) ?? []
      list.push({ startMin: minutesOf(span.startTime), endMin: minutesOf(span.endTime) })
      byDate.set(span.date, list)
    }
    for (const runs of byDate.values()) {
      for (const run of mergeSpans(runs)) {
        if (run.endMin - run.startMin < bulkLength) return true
      }
    }
    return false
  }, [selected, bulkLength])

  /** Only the coloured tags actually on screen get a legend entry. */
  const tagsInWeek = useMemo(() => {
    const seen = new Map<string, string>()
    for (const b of visibleBookings) {
      const client = b.clientId ? clientById.get(b.clientId) : undefined
      const painter = coloringTag(client, tagColors)
      if (painter) seen.set(painter, tagColors.get(painter)!)
    }
    return [...seen.entries()]
  }, [visibleBookings, clientById, tagColors])

  const thisYear = Number(today.slice(0, 4))
  const showsToday = view === 'week' ? days.includes(today) : today.slice(0, 7) === anchor.slice(0, 7)
  const label = periodLabel(view, days, year, month, thisYear)
  const unit = view === 'week' ? 'tydzień' : 'miesiąc'

  const startSelecting = () => {
    if (view === 'month') setView('week')
    setSelectMode(true)
    setSelected(new Set())
  }
  const stopSelecting = () => {
    setSelectMode(false)
    setSelected(new Set())
    bulk.reset()
  }

  /* ── top bar parts (and the same, inside the panel, on a phone) ───── */
  const periodNav = (
    <div className="inline-flex items-center gap-1">
      <button type="button" onClick={() => shift(-1)} aria-label={view === 'week' ? 'Poprzedni tydzień' : 'Poprzedni miesiąc'} className={ARROW}>
        ‹
      </button>
      <span className="font-semibold whitespace-nowrap">{label}</span>
      <button type="button" onClick={() => shift(1)} aria-label={view === 'week' ? 'Następny tydzień' : 'Następny miesiąc'} className={ARROW}>
        ›
      </button>
      {!showsToday && (
        <button type="button" onClick={() => setAnchor(today)} className="cl-link-quiet ml-2 text-class-gloss" title={`Wróć na ${unit} z dzisiejszym dniem`}>
          Dziś
        </button>
      )}
    </div>
  )
  const viewTabs = (
    <Tabs
      label="Widok kalendarza"
      value={view}
      onChange={setView}
      items={[
        { value: 'week', label: 'Tydzień' },
        { value: 'month', label: 'Miesiąc' },
      ]}
    />
  )
  const copyLink =
    view === 'week' ? (
      <button type="button" onClick={() => setCopyingWeek(true)} className="cl-link text-class-gloss whitespace-nowrap">
        Kopiuj tydzień
      </button>
    ) : null

  /* ── bottom bar ──────────────────────────────────────────────────── */
  const notices: ReactNode[] = []
  if (pastRefused)
    notices.push(
      <p key="past" role="alert" className="m-0 flex flex-wrap items-center gap-x-3 text-class-gloss text-class-ink-soft">
        <span>
          <span className="font-semibold text-class-wrong">✗</span> Te godziny już minęły — zaznaczyć można tylko te, które dopiero będą.
        </span>
        <button
          type="button"
          className="cl-link"
          onClick={() => {
            setAnchor(addDays(days[0]!, 7))
            setPastRefused(false)
          }}
        >
          Następny tydzień ›
        </button>
      </p>,
    )
  if (selectMode && selected.size > 0 && tooShortForLesson)
    notices.push(
      <p key="short" role="alert" className="m-0 text-class-gloss text-class-ink-soft">
        <span className="font-semibold text-class-wrong">✗</span> Zaznaczony blok jest krótszy niż {SLOT_LENGTH_LABELS[bulkLength]} — nikt by go nie
        zarezerwował. Zaznacz więcej godzin pod rząd albo wybierz krótszą lekcję.
      </p>,
    )
  if (bulk.error instanceof ApiError)
    notices.push(
      <ErrorLine key="bulk">
        {bulk.error.message === 'shorter_than_lesson'
          ? 'Blok jest krótszy niż jedna lekcja.'
          : bulk.error.message === 'in_the_past'
            ? 'Te godziny już minęły — nie da się ich pokazać na stronie.'
            : 'Nie udało się zapisać. Spróbuj jeszcze raz.'}
      </ErrorLine>,
    )

  let bar: ReactNode
  if (view === 'week' && selectMode) {
    bar =
      selected.size === 0 ? (
        <>
          <p className="m-0 min-w-0 flex-1 text-class-gloss text-class-ink-soft">
            Kliknij albo przeciągnij po godzinach, potem wybierz, co z nimi zrobić.
          </p>
          <button type="button" onClick={stopSelecting} className="cl-link-quiet ml-auto text-class-gloss whitespace-nowrap">
            Koniec zaznaczania
          </button>
        </>
      ) : (
        <>
          <span className="font-bold whitespace-nowrap">
            {selected.size} {plural(selected.size, 'godzina', 'godziny', 'godzin')}
          </span>
          <label className="flex items-center gap-2 text-class-gloss text-class-ink-soft whitespace-nowrap">
            Lekcje po
            <select
              value={bulkLength}
              onChange={(e) => setBulkLength(Number(e.target.value) as SlotLength)}
              className="cl-input min-h-10 w-auto pr-8 text-class-gloss"
            >
              {SLOT_LENGTHS.map((len) => (
                <option key={len} value={len}>
                  {SLOT_LENGTH_LABELS[len]}
                </option>
              ))}
            </select>
          </label>
          <button type="button" onClick={() => setSelected(new Set())} className="cl-link-quiet text-class-gloss">
            Odznacz
          </button>
          <span className="ml-auto flex flex-wrap items-center gap-3">
            <button type="button" onClick={stopSelecting} className="cl-link-quiet text-class-gloss whitespace-nowrap">
              Koniec zaznaczania
            </button>
            <Button variant="secondary" className={SMALL_BTN} disabled={bulk.isPending} onClick={() => bulk.mutate('clear')} title="Zwykła pusta godzina">
              Wyczyść
            </Button>
            <Button
              variant="secondary"
              className={`${SMALL_BTN} gap-2`}
              disabled={bulk.isPending}
              onClick={() => bulk.mutate('off')}
              title="Twój czas wolny — nikt go nie zarezerwuje"
            >
              <Swatch style={hatch(timeOffColor, true)} />
              Nie pracuję
            </Button>
            <Button
              className={SMALL_BTN}
              disabled={bulk.isPending || tooShortForLesson}
              onClick={() => bulk.mutate('open')}
              title={tooShortForLesson ? 'Zaznaczony blok jest krótszy niż jedna lekcja' : 'Te godziny będzie można zarezerwować na stronie'}
            >
              {bulk.isPending ? 'Zapisuję…' : 'Pokaż na stronie'}
            </Button>
          </span>
        </>
      )
  } else {
    bar = (
      <>
        {view === 'week' ? (
          <WeekLegend timeOffColor={timeOffColor} tags={tagsInWeek} />
        ) : (
          <div className="flex min-w-0 flex-wrap items-center gap-x-6 gap-y-1.5 text-class-label text-class-ink-soft desk:flex-1">
            <span className="inline-flex items-center gap-2"><Dot tone="info" />Lekcje</span>
            <span className="inline-flex items-center gap-2"><Dot tone="wait" />Prośby do potwierdzenia</span>
            <span className="inline-flex items-center gap-2"><Swatch style={hatch(timeOffColor, true)} />Nie pracuję</span>
            <span>Dwuklik na dniu otwiera tydzień</span>
          </div>
        )}
        <Button className={`${SMALL_BTN} ml-auto px-5 whitespace-nowrap`} onClick={startSelecting}>
          Zaznacz godziny
        </Button>
      </>
    )
  }

  const bottom = (
    <div className="flex w-full flex-col gap-2 py-1">
      {notices}
      <div className="flex w-full flex-wrap items-center gap-x-4 gap-y-3 desk:flex-nowrap">{bar}</div>
    </div>
  )

  const summary = isLoading
    ? 'Wczytuję…'
    : `${visibleBookings.length} ${
        view === 'month'
          ? `${plural(visibleBookings.length, 'rezerwacja', 'rezerwacje', 'rezerwacji')} w tym miesiącu`
          : `${plural(visibleBookings.length, 'lekcja', 'lekcje', 'lekcji')} w tym tygodniu`
      }` +
      (view === 'week'
        ? ` · ${bookableHours.size} ${plural(bookableHours.size, 'godzina wolna', 'godziny wolne', 'godzin wolnych')} na stronie`
        : '') +
      (pendingCount > 0 ? ` · ${pendingCount} do potwierdzenia` : '')

  return (
    <>
      <Column
        density="studio"
        top={
          <>
            <TopTitle studio>Kalendarz</TopTitle>
            <div className="ml-3">{periodNav}</div>
            <div className="ml-2 pt-2.5">{viewTabs}</div>
            {copyLink && <div className="ml-auto">{copyLink}</div>}
          </>
        }
        bottom={bottom}
      >
        <main className="px-5 pt-6 pb-10 desk:px-8 desk:pt-7">
          {/* Phone: the top bar is hidden, so its controls open the panel. */}
          <div className="mb-6 flex flex-col gap-3 desk:hidden">
            <h1 className="cl-title m-0">Kalendarz</h1>
            {periodNav}
            <div className="flex items-end gap-4">
              {viewTabs}
              {copyLink && <div className="ml-auto pb-1.5">{copyLink}</div>}
            </div>
          </div>

          {isError && (
            <div className="mb-6">
              <ErrorLine>Nie udało się wczytać kalendarza. Odśwież stronę.</ErrorLine>
            </div>
          )}

          {view === 'week' && problemHours.size > 0 && (
            <div className="mb-6 flex max-w-[760px] flex-col gap-1">
              <p className="m-0 flex items-center gap-2 font-semibold">
                <Dot tone="wait" />
                {problemHours.size}{' '}
                {plural(problemHours.size, 'otwarta godzina nie pojawi', 'otwarte godziny nie pojawią', 'otwartych godzin nie pojawi')} się na
                stronie
              </p>
              <p className="m-0 pl-3.5 text-class-gloss text-class-ink-soft">
                W kalendarzu mają dopisek „nie na stronie”. Powód jest zwykle jeden z dwóch: blok jest krótszy niż długość lekcji, którą dla
                niego wybrałaś (np. jedna godzina przy lekcji 1,5-godzinnej), albo zaczyna się za mniej niż {data?.minNoticeHours ?? 12} godzin —
                tyle wyprzedzenia wymaga rezerwacja. Otwórz dłuższy blok albo wybierz krótszą lekcję.
              </p>
            </div>
          )}

          {view === 'month' ? (
            <MonthGrid
              cells={cells}
              today={today}
              byDay={byDay}
              timeOffColor={timeOffColor}
              onOpen={setOpenDay}
              onOpenWeek={(iso) => {
                setAnchor(iso)
                setView('week')
                setOpenDay(null)
              }}
            />
          ) : (
            <StudioWeekGrid
              days={days}
              startHour={bounds.start}
              endHour={bounds.end}
              today={today}
              blocks={blocks}
              isPast={(day, hour) => hourHasPassed(day, hour, nowClock)}
              onCellClick={(day, hour) => setOpenHour({ day, hour })}
              selectMode={selectMode}
              isSelected={(day, hour) => selected.has(cellKey(day, hour))}
              canSelect={(day, hour) => !hourHasPassed(day, hour, nowClock)}
              onPickClick={togglePick}
              onNothingSelectable={refusePast}
              onSelectRange={onSelectRange}
            />
          )}

          {/* What the screen means, under the grid — the mockup keeps the top clear. */}
          <section className="mt-10 flex max-w-[760px] flex-col gap-3 border-t border-class-line pt-6 text-class-gloss text-class-ink-soft">
            <p className="m-0 font-semibold text-class-ink">{summary}</p>
            <p className="m-0">
              {view === 'month'
                ? 'Kliknij dzień, żeby otworzyć go na zapisy. Dwuklik otwiera jego tydzień.'
                : 'Kliknij godzinę, żeby wpisać ucznia albo otworzyć ją na zapisy. ⌘-klik (Ctrl na Windows) zaznacza pojedyncze godziny, także rozrzucone po tygodniu.'}{' '}
              Godziny są w Twojej strefie ({OWNER_TIMEZONE}).
            </p>
            {view === 'week' && (
              <p className="m-0">
                Po zaznaczeniu godzin: <strong className="font-semibold text-class-ink">Pokaż na stronie</strong> — te godziny staną się wolnymi
                terminami do rezerwacji. <strong className="font-semibold text-class-ink">Nie pracuję</strong> — Twój czas wolny, nigdy do
                wzięcia. <strong className="font-semibold text-class-ink">Wyczyść</strong> — zwykła pusta godzina. Umówione lekcje zostają w
                kalendarzu — odwołujesz je osobno. Godzin, które już minęły, nie da się zaznaczyć: nikt nie zarezerwuje wczorajszego
                popołudnia.
              </p>
            )}
            <div className="mt-2 flex flex-col gap-2">
              <p className="cl-label m-0">Kolor godzin, w których nie pracujesz</p>
              <ColorPicker classroom value={timeOffColor} onChange={(hex) => saveColor.mutate(hex)} />
              <p className="m-0 text-class-label text-class-ink-faint">
                Kolory uczniów i grup ustawiasz w zakładce <Link to="/studio/klienci">Uczniowie</Link> → Tagi i kolory.
              </p>
              {saveColor.isError && <ErrorLine>Nie udało się zapisać koloru.</ErrorLine>}
            </div>
          </section>
        </main>
      </Column>

      {openDay && (
        <DayEditor
          date={openDay}
          windows={byDay[openDay]?.windows ?? []}
          bookings={byDay[openDay]?.bookings ?? []}
          planItems={data?.planItems ?? []}
          onClose={() => setOpenDay(null)}
          onAdd={(startTime, endTime, slotMinutes: SlotLength) => addWindow.mutate({ date: openDay, startTime, endTime, slotMinutes })}
          onDelete={(id) => deleteWindow.mutate(id)}
          busy={addWindow.isPending || deleteWindow.isPending}
          error={
            addWindow.error instanceof ApiError
              ? addWindow.error.message === 'in_the_past'
                ? 'Ta godzina już minęła — nikt jej nie zarezerwuje.'
                : 'Nie udało się dodać. Sprawdź godziny.'
              : deleteWindow.error
                ? 'Nie udało się usunąć.'
                : ''
          }
        />
      )}

      {copyingWeek && (
        <CopyWeekDialog
          sourceMonday={days[0]!}
          bookings={data?.bookings ?? []}
          clients={clientsQuery.data ?? []}
          onClose={() => setCopyingWeek(false)}
          onDone={invalidate}
        />
      )}

      {openHour && (
        <HourEditor
          day={openHour.day}
          hour={openHour.hour}
          windows={(byDay[openHour.day]?.windows ?? []).filter(
            (w) => minutesOf(w.startTime) < (openHour.hour + 1) * 60 && minutesOf(w.endTime) > openHour.hour * 60,
          )}
          bookings={(byDay[openHour.day]?.bookings ?? []).filter((b) => {
            const s = wallClockIn(b.startsAt, OWNER_TIMEZONE).minutes
            const e = wallClockIn(b.endsAt, OWNER_TIMEZONE)
            const endMin = e.day === openHour.day ? e.minutes : 24 * 60
            return s < (openHour.hour + 1) * 60 && endMin > openHour.hour * 60
          })}
          timeOff={(byDay[openHour.day]?.timeOff ?? []).filter(
            (t) => minutesOf(t.startTime) < (openHour.hour + 1) * 60 && minutesOf(t.endTime) > openHour.hour * 60,
          )}
          timeOffColor={timeOffColor}
          planItems={data?.planItems ?? []}
          clients={clientsQuery.data ?? []}
          onClose={() => setOpenHour(null)}
          onChanged={invalidate}
          onOpenWindow={(startTime, endTime, slotMinutes) => addWindow.mutate({ date: openHour.day, startTime, endTime, slotMinutes })}
          onDeleteWindow={(id) => deleteWindow.mutate(id)}
        />
      )}
    </>
  )
}

const ARROW =
  'inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-class-sm border-0 bg-transparent font-class-text text-class-subheading text-class-ink-soft hover:bg-class-white/70 hover:text-class-ink'

/** The mockup's legend: what each block means, then what the dot means. */
function WeekLegend({ timeOffColor, tags }: { timeOffColor: string; tags: [string, string][] }) {
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-x-6 gap-y-1.5 text-class-label text-class-ink-soft desk:flex-1">
      <span className="inline-flex items-center gap-2">
        <Swatch className="bg-class-field-soft shadow-[inset_0_0_0_1px_var(--color-class-sky-200)]" />
        Lekcja
      </span>
      <span className="inline-flex items-center gap-2">
        <Swatch className="border border-dashed border-[#b9d4ea] bg-class-white" />
        Otwarte na zapisy
      </span>
      <span className="inline-flex items-center gap-2">
        <Swatch className="bg-class-gold-100 shadow-[inset_0_0_0_1px_var(--color-class-gold-line)]" />
        Prośba do potwierdzenia
      </span>
      <span className="inline-flex items-center gap-2">
        <Swatch style={hatch(timeOffColor, true)} />
        Nie pracuję
      </span>
      {tags.length === 0 ? (
        <span className="desk:ml-2">Kropka = grupa ucznia (tag)</span>
      ) : (
        <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1 desk:ml-2">
          Kropka = grupa ucznia:
          {tags.map(([tag, color]) => (
            <span key={tag} className="inline-flex items-center gap-1.5">
              <span aria-hidden className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: color }} />
              {tag}
            </span>
          ))}
        </span>
      )}
    </div>
  )
}

/** The month: a ruled 7-column sheet in the week's language. */
function MonthGrid({
  cells,
  today,
  byDay,
  timeOffColor,
  onOpen,
  onOpenWeek,
}: {
  cells: { iso: string; day: number; inMonth: boolean; isToday: boolean }[]
  today: string
  byDay: Record<string, { windows: AvailabilityWindow[]; bookings: Booking[]; timeOff: TimeOff[] }>
  timeOffColor: string
  onOpen: (iso: string) => void
  onOpenWeek: (iso: string) => void
}) {
  return (
    <div className="overflow-x-auto pb-2">
      <div className="min-w-[640px]">
        <div className="grid grid-cols-7">
          {cells.slice(0, 7).map((c) => (
            <div key={c.iso} className="px-1 pb-3 text-class-label font-semibold text-class-ink-faint">
              {weekdayHeading(c.iso).weekday}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 border-t border-r border-class-line">
          {cells.map((cell) => {
            const day = byDay[cell.iso]
            const off = (day?.timeOff.length ?? 0) > 0
            const pending = day?.bookings.filter((b) => b.status === 'pending').length ?? 0
            const lessons = (day?.bookings.length ?? 0) - pending
            const past = cell.iso < today

            return (
              <button
                key={cell.iso}
                type="button"
                onClick={() => onOpen(cell.iso)}
                onDoubleClick={() => onOpenWeek(cell.iso)}
                className={[
                  'relative flex min-h-[104px] cursor-pointer flex-col items-start gap-1 border-0 border-b border-l border-solid border-class-line p-2 text-left font-class-text transition-colors hover:bg-class-field-soft',
                  past ? 'bg-class-line/45' : '',
                ].join(' ')}
              >
                <span
                  className={[
                    'inline-flex items-center justify-center font-class-display text-class-subheading font-medium leading-none',
                    cell.isToday ? 'h-7 min-w-7 rounded-full bg-class-sky-200 px-1.5 text-class-sky-700' : 'h-7 text-class-ink',
                    cell.inMonth ? '' : 'opacity-40',
                  ].join(' ')}
                >
                  {cell.day}
                  {cell.isToday && <span className="sr-only"> (dziś)</span>}
                </span>

                {day?.windows.length ? (
                  <span className={['text-class-label leading-snug font-semibold text-class-sky-700', cell.inMonth ? '' : 'opacity-40'].join(' ')}>
                    Wolne {day.windows.map((w) => `${hhmm(w.startTime)}–${hhmm(w.endTime)}`).join(', ')}
                  </span>
                ) : null}

                {off && (
                  <span className={['inline-flex items-center gap-1.5 text-class-label text-class-ink-faint', cell.inMonth ? '' : 'opacity-40'].join(' ')}>
                    <Swatch style={hatch(timeOffColor, true)} />
                    Nie pracuję
                  </span>
                )}

                {(lessons > 0 || pending > 0) && (
                  <span className={['mt-auto flex flex-col gap-0.5', cell.inMonth ? '' : 'opacity-40'].join(' ')}>
                    {lessons > 0 && (
                      <Status tone="info">
                        {lessons} {plural(lessons, 'lekcja', 'lekcje', 'lekcji')}
                      </Status>
                    )}
                    {pending > 0 && (
                      <Status tone="wait">
                        {pending} {plural(pending, 'prośba', 'prośby', 'próśb')}
                      </Status>
                    )}
                  </span>
                )}
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export { startOfWeek }
